import {
  AcceptChallengeResultTypenames,
  ChallengeType,
  Query,
  SelectChallengeResultTypenames,
} from '@varo/graphql-schema-types';
import {
  Href,
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import useAcceptChallengeGql from 'features/otp/hooks/useAcceptChallengeGql';
import useSelectChallengeGql from 'features/otp/hooks/useSelectChallengeGql';
import OtpScreen from 'features/otp/OtpScreen';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GetProfileInfo from 'services/graphql/queries/GetProfileInfo';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { useQuery } from 'urql';

interface SearchParams extends Record<string, string | string[]> {
  cardId: string;
  challengeId: string;
  challengeType: string;
}

const isSupportedChallengeType = (
  value: string,
): value is ChallengeType.Email | ChallengeType.Sms =>
  value === ChallengeType.Email || value === ChallengeType.Sms;

const CardCredentialOtpScreen = ({
  returnPath,
  errorReturnToCardDetail,
}: {
  returnPath?: string;
  errorReturnToCardDetail?: boolean;
}) => {
  const router = useRouter();
  const { t: tCardManagement } = useTranslation('cardManagement');
  const { cardId, challengeId, challengeType } =
    useLocalSearchParams<SearchParams>();
  const hasRequiredParams = Boolean(cardId && challengeId);
  const supportedChallengeType = isSupportedChallengeType(challengeType)
    ? challengeType
    : null;
  const [activeChallengeId, setActiveChallengeId] = useState(challengeId ?? '');
  const [screenLevelError, setScreenLevelError] = useState<{
    title?: string;
    message?: string;
  } | null>(null);

  const { acceptChallenge, fetching: acceptFetching } = useAcceptChallengeGql();
  const { selectChallenge, fetching: resendFetching } = useSelectChallengeGql();

  const { analyticsEvents } = useAnalyticsConfiguration();

  useFocusEffect(
    useCallback(() => {
      void analyticsEvents.logPressableEventFromConfig(
        'cardCredentialOtpStarted',
        null,
      );
    }, [analyticsEvents]),
  );

  const handleOtpExpired = useCallback(() => {
    void analyticsEvents.logPressableEventFromConfig(
      'cardCredentialOtpExpired',
      null,
    );
  }, [analyticsEvents]);

  const handleErrorDone = useCallback(() => {
    if (returnPath && !errorReturnToCardDetail) {
      router.dismissTo(returnPath as Href);
    } else {
      router.dismissTo({
        pathname: '/myvaro/card/[cardId]',
        params: { cardId },
      });
    }
  }, [errorReturnToCardDetail, returnPath, cardId, router]);

  const handleScreenLevelError = useCallback(
    (error: { errorCode: string; title?: string; message?: string }) => {
      setScreenLevelError({ title: error.title, message: error.message });
    },
    [],
  );

  useBackHandler(
    useCallback(() => {
      if (screenLevelError) {
        handleErrorDone();
        return true;
      }
      return false;
    }, [screenLevelError, handleErrorDone]),
  );

  const [{ data: profileData }] = useQuery<Query>({
    query: GetProfileInfo,
    requestPolicy: 'cache-only',
  });

  const contactValue = useMemo(() => {
    if (supportedChallengeType === ChallengeType.Sms) {
      const phone = profileData?.user?.customerInfo?.phone ?? '';
      const lastFour = phone.slice(-4);
      return lastFour ? ` ending in ${lastFour}` : '';
    }
    if (supportedChallengeType === ChallengeType.Email) {
      const email = profileData?.user?.customerInfo?.email?.address ?? '';
      return email ? ` at ${email}` : '';
    }
    return '';
  }, [supportedChallengeType, profileData]);

  const handleVerifyOtp = async (
    code: string,
  ): Promise<{
    success?: boolean;
    errorCode?: number | string;
    title?: string;
    message?: string;
  }> => {
    void analyticsEvents.logPressableEventFromConfig(
      'cardCredentialOtpEntered',
      null,
    );
    try {
      const result = await acceptChallenge({
        input: {
          challengeId: activeChallengeId,
          acceptOptions: {
            otpOption: {
              otpCode: code,
            },
          },
        },
      });
      const response = result.data?.acceptChallenge;

      if (
        response?.__typename ===
          AcceptChallengeResultTypenames.AcceptChallengeSuccess &&
        response.challengeVerified
      ) {
        void analyticsEvents.logPressableEventFromConfig(
          'cardCredentialOtpPassed',
          null,
        );
        const challengeCompletedParams = {
          challengeJustCompleted: 'true',
          completedChallengeId: response.challengeId,
        };
        if (returnPath) {
          router.dismissTo({
            pathname: returnPath,
            params: challengeCompletedParams,
          } as Href);
        } else {
          router.dismissTo({
            pathname: '/myvaro/card/[cardId]',
            params: {
              cardId,
              ...challengeCompletedParams,
            },
          });
        }
        return { success: true };
      }

      if (
        response?.__typename ===
        AcceptChallengeResultTypenames.AcceptChallengeError
      ) {
        void analyticsEvents.logPressableEventFromConfig(
          'cardCredentialOtpFailed',
          {
            error_type: 'incorrect_format',
            error_description: response.message ?? '',
          },
        );
        return {
          errorCode: Number(response.code),
          message: response.message,
          title: response.title,
        };
      }

      return { errorCode: 500 };
    } catch (error) {
      if (error instanceof AbortError) {
        return {};
      }
      return { errorCode: 500 };
    }
  };

  const handleResendOtp = async (): Promise<{
    errorCode?: number | string;
    title?: string;
    message?: string;
  }> => {
    try {
      const result = await selectChallenge(activeChallengeId);
      const response = result.data?.selectChallenge;

      if (
        response?.__typename ===
        SelectChallengeResultTypenames.SelectChallengeSuccess
      ) {
        setActiveChallengeId(response.challengeId);
        return {};
      }

      if (
        response?.__typename ===
        SelectChallengeResultTypenames.SelectChallengeError
      ) {
        return {
          errorCode: Number(response.code),
          message: response.message,
          title: response.title,
        };
      }

      return { errorCode: 500 };
    } catch (error) {
      if (error instanceof AbortError) {
        return {};
      }
      return { errorCode: 500 };
    }
  };

  if (screenLevelError) {
    return (
      <>
        <Stack.Screen
          options={{
            headerShown: false,
            gestureEnabled: false,
          }}
        />
        <ErrorScreen
          screenType={ErrorScreenType.Error}
          messageHeadline={screenLevelError.title}
          messageBody={screenLevelError.message}
          primaryButton={{
            title: tCardManagement('credentialChallenge.declined.doneButton'),
            onPress: handleErrorDone,
          }}
        />
      </>
    );
  }

  if (!hasRequiredParams || !supportedChallengeType) {
    return (
      <ErrorScreen
        screenType={ErrorScreenType.Error}
        messageHeadline={tCardManagement(
          'credentialChallenge.confirmIdentity.error.title',
        )}
        messageBody={tCardManagement(
          'credentialChallenge.confirmIdentity.error.message',
        )}
        primaryButton={{
          title: tCardManagement(
            'credentialChallenge.confirmIdentity.error.backButton',
          ),
          onPress: () => router.back(),
        }}
      />
    );
  }

  const isEmail = supportedChallengeType === ChallengeType.Email;
  const verificationType = isEmail
    ? OtpVerificationType.EMAIL
    : OtpVerificationType.PHONE;
  const header = tCardManagement(
    isEmail
      ? 'credentialChallenge.otp.emailHeader'
      : 'credentialChallenge.otp.phoneHeader',
  );
  const description = tCardManagement(
    isEmail
      ? 'credentialChallenge.otp.emailDescription'
      : 'credentialChallenge.otp.phoneDescription',
    { contactValue },
  );

  return (
    <OtpScreen
      flowKey={OtpFlowKey.CARD_CREDENTIALS}
      verificationType={verificationType}
      headerText={header}
      submitButtonTestID="card-credential-otp-submit-button"
      description={description}
      isLoading={acceptFetching || resendFetching}
      onVerifyOtp={handleVerifyOtp}
      onResendOtp={handleResendOtp}
      onBack={() => router.back()}
      onCodeExpired={handleOtpExpired}
      onScreenLevelError={handleScreenLevelError}
      isCode
    />
  );
};

export default CardCredentialOtpScreen;
